import { Button, Card, Input, Select, Tag, message } from 'antd';
import LOCALS from 'commons/locals';
import {
  OrderDeliveryTypeMap,
  OrderDeliveryTypeOptionList,
  findLabelByValue,
} from 'commons/options';
import CopyButton from 'components/copy-button';
import LinkButton from 'components/link-button';
import { Trans } from 'react-i18next';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { OmsOrderDetail } from 'types/oms';
import DualTimezoneTime from 'components/dual-timezone-time';
import i18n from 'i18n';
import { OMS_ORDER_TAG_OPTION_LIST } from 'constant';
import { updateOmsOrderDetail } from 'apis/oms';
import { getOmsOrderDetailV2 } from 'apis/oms';
import { UnwrapPromise } from 'types/base';
import { useEffect, useMemo, useState } from 'react';
import useResource from 'commons/hooks/useResource';
import { CircleCheck, Info } from 'lucide-react';
import { selectUserInfo } from 'store/slices/userInfoSlice';
import { ORDER_CREATED_FROM_OPTION_LIST } from 'constant/order-create-source';
import {
  getOrderStatusConfig,
  ORDER_STATUS_MAP,
} from 'constant/oms-order-status';

type OrderBaseInfoProps = {
  stockPlace?: string;
  omsOrderDetail: OmsOrderDetail & {
    tagList: {
      id: number;
      tag: string;
    }[];
    coupon: UnwrapPromise<ReturnType<typeof getOmsOrderDetailV2>>['coupon'];
  };
};

const OrderBaseInfo = ({ omsOrderDetail, stockPlace }: OrderBaseInfoProps) => {
  const userInfo = useAppSelector(selectUserInfo);

  const {
    status,
    orderSn,
    invoiceNo,
    memberUsername,
    createdFrom,
    deliveryCompany,
    deliverySn,
    integration,
    memberId,
    multiplePayStatus,
    deliveryType,
    deliveryTime,
    createTime,
    id,
    note,
    tagList,
    coupon,
  } = omsOrderDetail;
  const { orderStatusOptions, orderTypeOptions, staffSelectOptions } =
    useAppSelector(selectGlobalInfo);

  const haveRight = useResource('order-detail-update-staff-name');
  const haveRightWhenCompleted = useResource(
    'order-detail-update-staff-name-completed',
  );

  const [draftNote, setDraftNote] = useState(note || '');
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    setDraftNote(note || '');
  }, [note]);

  const couponDescription = useMemo(() => {
    if (!coupon) return null;
    if (coupon.i18nDescription) {
      const lang = i18n.language.replace('_', '-');
      if (coupon.i18nDescription[lang]) {
        return (
          <div className="text-red-500">
            <h3>{coupon.i18nDescription[lang].title}</h3>
            <div>{coupon.i18nDescription[lang].description}</div>
          </div>
        );
      }
    }
    return coupon.note || '-';
  }, [coupon]);

  const baseInfoList = useMemo(() => {
    const list: {
      label: string | React.ReactNode;
      node: React.ReactNode;
      key: string;
    }[] = [];

    /**
     *  ===============================================
     * 基础信息
     */
    list.push({
      key: 'order_status',
      label: <Trans i18nKey={LOCALS.order_status} />,
      node: (
        <Tag color={getOrderStatusConfig(status).color}>
          {findLabelByValue(status, orderStatusOptions)}
        </Tag>
      ),
    });

    list.push({
      key: 'order_id',
      label: <Trans i18nKey={LOCALS.dwawuQNUEi}></Trans>,
      node: <CopyButton copyText={id.toString()}>{id}</CopyButton>,
    });

    list.push({
      key: 'order_sn',
      label: <Trans i18nKey={LOCALS.order_sn} />,
      node: <CopyButton copyText={orderSn}>{orderSn}</CopyButton>,
    });

    invoiceNo &&
      list.push({
        key: 'invoice_no',
        label: 'Invoice No',
        node: <CopyButton copyText={invoiceNo}>{invoiceNo}</CopyButton>,
      });

    list.push({
      key: 'created_time',
      label: <Trans i18nKey={LOCALS.created_time} />,
      node: <DualTimezoneTime time={createTime} stockPlace={stockPlace} />,
    });

    list.push({
      key: 'member_account',
      label: <Trans i18nKey={LOCALS.member_account} />,
      node:
        memberId && memberUsername ? (
          <CopyButton copyText={memberUsername}>
            <LinkButton href={`/ums/member-view/${memberId}`}>
              {memberUsername}
            </LinkButton>
          </CopyButton>
        ) : (
          '-'
        ),
    });

    list.push({
      key: 'created_from',
      label: <Trans i18nKey={LOCALS.created_from} />,
      node: userInfo.isAdmin ? (
        <Select
          placeholder={i18n.t(LOCALS.please_select)}
          className="w-40"
          value={createdFrom}
          onChange={(val) => {
            updateOmsOrderDetail(id, { createdFrom: val }).then(() => {
              window.location.reload();
            });
          }}
          options={ORDER_CREATED_FROM_OPTION_LIST}
        />
      ) : (
        findLabelByValue(createdFrom, ORDER_CREATED_FROM_OPTION_LIST)
      ),
    });

    list.push({
      key: 'points_for_order',
      label: <Trans i18nKey={LOCALS.points_for_order} />,
      node: integration || 0,
    });

    list.push({
      key: 'order_type',
      label: <Trans i18nKey={LOCALS.order_type} />,
      node: findLabelByValue(omsOrderDetail.orderType, orderTypeOptions),
    });

    list.push({
      key: 'tag_list',
      label: <Trans i18nKey={LOCALS.khvsAbbLJs} />,
      node: (
        <Select
          mode="multiple"
          value={tagList.map((i) => i.tag)}
          onChange={async (val) => {
            await updateOmsOrderDetail(id, { tagList: val });
            window.location.reload();
          }}
          placeholder={i18n.t(LOCALS.please_select)}
          options={OMS_ORDER_TAG_OPTION_LIST}
          style={{ width: 150 }}
        />
      ),
    });

    list.push({
      key: 'staff_name',
      label: <Trans i18nKey={LOCALS.staff} />,
      node: (
        <Select
          placeholder={i18n.t(LOCALS.please_select)}
          value={omsOrderDetail.staffName}
          className="w-40"
          allowClear
          showSearch
          disabled={
            status === ORDER_STATUS_MAP.COMPLETED
              ? !haveRightWhenCompleted
              : !haveRight
          }
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          onChange={async (val) => {
            val = val || '';
            await updateOmsOrderDetail(id, { staffName: val });
            window.location.reload();
          }}
          options={staffSelectOptions
            .filter((i) => i.status === 1)
            .map((i) => {
              return {
                value: i.label,
                label: i.label,
              };
            })}
        ></Select>
      ),
    });

    /**
     *  ===============================================
     * 条件展示
     */
    !!deliveryCompany &&
      list.push({
        key: 'delivery_method',
        label: <Trans i18nKey={LOCALS.delivery_method} />,
        node: deliveryCompany || '-',
      });
    !!deliverySn &&
      list.push({
        key: 'shipment_number',
        label: <Trans i18nKey={LOCALS.shipment_number} />,
        node: deliverySn || '-',
      });
    !!deliveryTime &&
      list.push({
        key: 'delivery_time',
        label: <Trans i18nKey={LOCALS.delivery_time} />,
        node: <DualTimezoneTime time={deliveryTime} stockPlace={stockPlace} />,
      });

    deliveryType === OrderDeliveryTypeMap.PICKUP &&
      list.push({
        key: 'delivery_type',
        label: <Trans i18nKey={LOCALS.vucsrXTzfQ} />,
        node: (
          <Tag
            color={
              OrderDeliveryTypeOptionList.find((d) => d.value === deliveryType)
                ?.color
            }
          >
            {findLabelByValue(deliveryType, OrderDeliveryTypeOptionList)}
          </Tag>
        ),
      });

    !!multiplePayStatus &&
      list.push({
        key: 'multiple_pay_status',
        label: <Trans i18nKey={LOCALS.MultiplePayList} />,
        node: <CircleCheck className="w-5 h-5 text-green-500" />,
      });

    !!coupon &&
      list.push({
        key: 'coupon',
        label: <Trans i18nKey={LOCALS.coupon} />,
        node: couponDescription,
      });

    list.push({
      key: 'note',
      label: <Trans i18nKey={LOCALS.remark} />,
      node: (
        <div className="flex flex-col gap-2 max-w-xl">
          <Input.TextArea
            value={draftNote}
            onChange={(e) => setDraftNote(e.target.value)}
            autoSize={{ minRows: 2, maxRows: 6 }}
            placeholder={i18n.t(LOCALS.please_enter_remark) || ''}
          />
          <div>
            <Button
              size="small"
              type="primary"
              loading={savingNote}
              disabled={draftNote === (note || '')}
              onClick={async () => {
                setSavingNote(true);
                try {
                  await updateOmsOrderDetail(id, { note: draftNote });
                  message.success(i18n.t(LOCALS.successful_operation));
                  window.location.reload();
                } finally {
                  setSavingNote(false);
                }
              }}
            >
              <Trans i18nKey={LOCALS.save} />
            </Button>
          </div>
        </div>
      ),
    });

    return list;
  }, [
    status,
    orderStatusOptions,
    id,
    orderSn,
    invoiceNo,
    createTime,
    memberId,
    memberUsername,
    userInfo.isAdmin,
    createdFrom,
    integration,
    omsOrderDetail.orderType,
    omsOrderDetail.staffName,
    orderTypeOptions,
    tagList,
    haveRight,
    haveRightWhenCompleted,
    staffSelectOptions,
    deliveryCompany,
    deliverySn,
    deliveryTime,
    deliveryType,
    multiplePayStatus,
    coupon,
    couponDescription,
    note,
    draftNote,
    savingNote,
    stockPlace,
  ]);

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="flex items-center gap-2 mb-4 text-base font-medium">
        <Info className="w-5 h-5" />
        <Trans i18nKey={LOCALS.basic_info} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-5">
        {baseInfoList.map((item) => {
          return (
            <div
              key={item.key}
              className={
                item.key === 'note'
                  ? 'col-span-2 md:col-span-3 lg:col-span-2'
                  : undefined
              }
            >
              <div className="text-sm font-medium text-gray-500">
                {item.label}
              </div>
              <div className="mt-1 text-sm font-medium">{item.node}</div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default OrderBaseInfo;
